import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, Users, RefreshCw, Download } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Logo } from "../components/Logo";
import { RESULTS } from "../data/quiz";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cc_elevate_admin_token";

const PROFILE_LABEL = { A: "Reattivo", B: "Intuitivo", C: "Orchestratore" };

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);

  const loadData = useCallback(async (tk) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tk}` };
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(l.data.leads);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Sessione scaduta, effettua di nuovo l'accesso.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Password non corretta");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setStats(null);
  };

  const download = (rows, name) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    const headers = ["Data", "Nome", "Cognome", "Email", "Prefisso", "Telefono", "Profilo", "Q1", "Q2", "Q3", "Q4", "Q5", "Q6", "Q7", "Email inviata", "Newsletter"];
    const rows = leads.map((l) => [
      l.created_at, l.first_name, l.last_name, l.email, l.phone_prefix, l.phone, PROFILE_LABEL[l.profile],
      l.answers.q1, l.answers.q2, l.answers.q3, l.answers.q4, l.answers.q5, l.answers.q6, l.answers.q7,
      l.email_sent ? "Si" : "No",
      l.marketing_accepted ? "Si" : "No",
    ]);
    download([headers, ...rows], "cc-elevate-leadership");
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4">
        <Logo className="h-24 mb-10" />
        <div className="relative gold-frame bg-[#0A0A0A] border border-white/10 px-8 py-10 w-full max-w-sm">
          <p className="text-xs font-bold tracking-[0.25em] text-gold uppercase text-center">Area Riservata</p>
          <h1 className="mt-4 font-serif text-3xl text-white text-center">Dashboard Admin</h1>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#111] border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold transition-colors duration-300"
            />
            {error && <p className="text-xs text-red-400" data-testid="admin-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-bold tracking-[0.15em] uppercase bg-gold text-black hover:bg-gold-hover transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 border-b border-white/10 pb-6">
          <div className="flex items-center gap-4">
            <Logo className="h-14" />
            <div>
              <p className="text-[11px] font-bold tracking-[0.25em] text-gold uppercase">Leadership Assessment</p>
              <h1 className="font-serif text-2xl text-white">Dashboard Lead</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCSV}
              disabled={leads.length === 0}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-black bg-gold hover:bg-gold-hover transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="h-3.5 w-3.5" /> Esporta CSV
            </button>
            <button
              onClick={() => loadData(token)}
              data-testid="refresh-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white/70 border border-white/15 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white/70 border border-white/15 hover:border-red-400 hover:text-red-400 transition-colors duration-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Lead Totali" value={stats.total} icon />
            <StatCard label="Reattivo (A)" value={stats.by_profile.A} />
            <StatCard label="Intuitivo (B)" value={stats.by_profile.B} />
            <StatCard label="Orchestratore (C)" value={stats.by_profile.C} />
          </div>
        )}

        {/* Chart */}
        {stats && stats.total > 0 && (
          <ChartCard
            title="Distribuzione Profili Leadership"
            data={[
              { name: "Reattivo", value: stats.by_profile.A },
              { name: "Intuitivo", value: stats.by_profile.B },
              { name: "Orchestratore", value: stats.by_profile.C },
            ]}
            testId="profile-chart"
          />
        )}

        {/* Table */}
        <div className="mt-8 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-gold/80 border-b border-white/10">
                <th className="px-4 py-3 font-bold">Data</th>
                <th className="px-4 py-3 font-bold">Nome</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Telefono</th>
                <th className="px-4 py-3 font-bold">Profilo</th>
                <th className="px-4 py-3 font-bold">Email inviata</th>
                <th className="px-4 py-3 font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40">Caricamento...</td></tr>
              )}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40" data-testid="no-leads">Nessun lead raccolto finora.</td></tr>
              )}
              {leads.map((lead) => (
                <tr key={lead.lead_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{fmtDate(lead.created_at)}</td>
                  <td className="px-4 py-3 text-white">{lead.first_name} {lead.last_name}</td>
                  <td className="px-4 py-3 text-white/80">{lead.email}</td>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{lead.phone ? `${lead.phone_prefix} ${lead.phone}` : "-"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider border border-gold/50 text-gold">
                      {PROFILE_LABEL[lead.profile]}
                    </span>
                  </td>
                  <td className="px-4 py-3">{lead.email_sent ? <span className="text-gold">Sì</span> : <span className="text-white/40">No</span>}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelected(lead)} data-testid={`view-lead-${lead.lead_id}`} className="text-xs font-bold uppercase tracking-wider text-gold hover:text-gold-hover">
                      Risposte
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
        >
          <div
            className="relative gold-frame bg-[#0A0A0A] border border-white/10 max-w-lg w-full max-h-[85vh] overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-detail-modal"
          >
            <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold">Dettaglio risposte</p>
            <h2 className="mt-2 font-serif text-2xl text-white">{selected.first_name} {selected.last_name}</h2>
            <p className="text-white/60 text-sm">{selected.email}</p>
            <p className="mt-1 text-gold text-sm">Profilo: {RESULTS[selected.profile].title}</p>
            <div className="mt-6 space-y-3">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <div key={n} className="flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="text-white/70 text-sm">Domanda {n}</span>
                  <span className="text-gold font-bold text-sm">{selected.answers[`q${n}`]}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setSelected(null)}
              className="mt-7 w-full px-6 py-3 text-xs font-bold tracking-[0.15em] uppercase bg-gold text-black hover:bg-gold-hover transition-colors duration-300"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ChartCard = ({ title, data, testId, colors = ["#8a6d1f", "#B5911F", "#D4AF37"] }) => (
  <div className="mt-4 border border-white/10 bg-[#0A0A0A] px-5 py-6">
    <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-gold mb-2">{title}</p>
    <div className="h-64 w-full" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} stroke="#000000">
            {colors.map((c, i) => (
              <Cell key={i} fill={c} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 13 }} itemStyle={{ color: "#D4AF37" }} />
          <Legend wrapperStyle={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const StatCard = ({ label, value, icon }) => (
  <div className="border border-white/10 bg-[#0A0A0A] px-5 py-5">
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-white/50">{label}</p>
      {icon && <Users className="h-4 w-4 text-gold" />}
    </div>
    <p className="mt-2 font-serif text-3xl text-gold">{value}</p>
  </div>
);
